/**
 * Search keys for a Firestore `user_profiles` document.
 *
 * The member directory and @mention search can't do case-insensitive or
 * substring matching in Firestore, so they range-query denormalised lowercase
 * copies of the name:
 *
 *     .where("name_lowercase", ">=", q).where("name_lowercase", "<=", q + "~")
 *
 * That makes these fields load-bearing, and they used to be derived by hand at
 * eight separate write sites — none of which ran when a profile was first
 * created. Members whose name was never edited therefore had no `name_lowercase`
 * at all, and a Firestore range query skips documents where the field is
 * missing: they were invisible to mention search while rendering perfectly
 * everywhere else.
 *
 * Deriving them in one place is what stops that drifting again.
 */

export interface ProfileNameInput {
  firstName?: string | null;
  lastName?: string | null;
  /**
   * Explicit display name, for the rename flow where the member picks their own
   * rather than it being first + last.
   */
  name?: string | null;
}

export interface ProfileSearchFields {
  name: string | null;
  name_lowercase: string | null;
  firstName_lowercase: string | null;
  lastName_lowercase: string | null;
}

/**
 * Trims and collapses internal runs of whitespace.
 *
 * A stray double space is not cosmetic here: `"chris  kerr"` sorts BEFORE
 * `"chris k"` (space is 0x20, "k" is 0x6B), so it falls outside the range query
 * and the member silently stops being findable.
 */
function normalizeWhitespace(value?: string | null): string {
  return `${value == null ? "" : value}`.replace(/\s+/g, " ").trim();
}

/**
 * Builds the search keys for a profile.
 *
 * Deliberately does NOT null out the whole name when only one half is present.
 * The previous `firstName && lastName ? ... : null` meant anyone without a last
 * name — common for Fender accounts and Discord imports — got `null` and
 * dropped out of every search. A first name alone is still worth searching by.
 */
export function buildProfileSearchFields(input: ProfileNameInput): ProfileSearchFields {
  const firstName = normalizeWhitespace(input && input.firstName);
  const lastName = normalizeWhitespace(input && input.lastName);
  const explicitName = normalizeWhitespace(input && input.name);

  const name = explicitName || [firstName, lastName].filter(Boolean).join(" ");

  return {
    name: name || null,
    name_lowercase: name ? name.toLowerCase() : null,
    firstName_lowercase: firstName ? firstName.toLowerCase() : null,
    lastName_lowercase: lastName ? lastName.toLowerCase() : null,
  };
}

/**
 * Whether a stored profile's search keys are missing or out of date.
 *
 * Used by the backfill to skip documents that are already correct, so a repeat
 * run reports nothing to do.
 */
export function profileSearchFieldsNeedUpdate(stored: any, wanted: ProfileSearchFields): boolean {
  if (!stored) return true;
  return stored.name_lowercase !== wanted.name_lowercase || stored.firstName_lowercase !== wanted.firstName_lowercase || stored.lastName_lowercase !== wanted.lastName_lowercase;
}
